import { z } from "zod";

export const LLM_MATERIALIZED_PROMPT_OPERATION_TYPES = [
  "generate_object",
  "generate_text",
  "stream_text",
  "generate_image",
] as const;

export const llmMaterializedPromptOperationTypeSchema = z.enum(
  LLM_MATERIALIZED_PROMPT_OPERATION_TYPES,
);
export type LlmMaterializedPromptOperationType = z.infer<
  typeof llmMaterializedPromptOperationTypeSchema
>;

export const llmMaterializedPromptImageMetadataV1Schema = z.object({
  label: z.string().trim().min(1).optional(),
  mediaType: z.string().trim().min(1).optional(),
  byteLength: z.number().int().nonnegative().optional(),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional(),
  objectKey: z.string().trim().min(1).optional(),
  sourceUrl: z.string().trim().min(1).optional(),
  canonicalAssetId: z.uuid().optional(),
  generatedAssetId: z.uuid().optional(),
});
export type LlmMaterializedPromptImageMetadataV1 = z.infer<
  typeof llmMaterializedPromptImageMetadataV1Schema
>;

export const llmMaterializedPromptContentPartV1Schema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({ type: z.literal("image"), image: llmMaterializedPromptImageMetadataV1Schema }),
  z.object({ type: z.literal("unknown"), summary: z.string().trim().min(1) }),
]);
export type LlmMaterializedPromptContentPartV1 = z.infer<
  typeof llmMaterializedPromptContentPartV1Schema
>;

export const llmMaterializedPromptMessageV1Schema = z.object({
  role: z.string().trim().min(1),
  content: z.union([z.string(), z.array(llmMaterializedPromptContentPartV1Schema)]),
});
export type LlmMaterializedPromptMessageV1 = z.infer<typeof llmMaterializedPromptMessageV1Schema>;

export const llmMaterializedPromptToolV1Schema = z.object({
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
});
export type LlmMaterializedPromptToolV1 = z.infer<typeof llmMaterializedPromptToolV1Schema>;

export const llmMaterializedPromptV1Schema = z
  .object({
    type: z.literal("llm.materialized_prompt"),
    version: z.literal(1),
    operationType: llmMaterializedPromptOperationTypeSchema,
    prompt: z.string().optional(),
    system: z.string().optional(),
    messages: z.array(llmMaterializedPromptMessageV1Schema).optional(),
    images: z.array(llmMaterializedPromptImageMetadataV1Schema).optional(),
    tools: z.array(llmMaterializedPromptToolV1Schema).optional(),
    options: z.record(z.string(), z.unknown()).optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.prompt && !value.system && !value.messages?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A materialized prompt must include prompt, system, or messages.",
      });
    }
  });
export type LlmMaterializedPromptV1 = z.infer<typeof llmMaterializedPromptV1Schema>;

const formatJson = (value: unknown): string => JSON.stringify(value, null, 2);

const formatImageMetadata = (image: LlmMaterializedPromptImageMetadataV1): string => {
  const parts = [
    image.label ? `label=${image.label}` : null,
    image.mediaType ? `mediaType=${image.mediaType}` : null,
    image.byteLength !== undefined ? `bytes=${image.byteLength}` : null,
    image.width && image.height ? `size=${image.width}x${image.height}` : null,
    image.objectKey ? `objectKey=${image.objectKey}` : null,
    image.sourceUrl ? `sourceUrl=${image.sourceUrl}` : null,
    image.canonicalAssetId ? `canonicalAssetId=${image.canonicalAssetId}` : null,
    image.generatedAssetId ? `generatedAssetId=${image.generatedAssetId}` : null,
  ].filter((part): part is string => part !== null);

  return `[image${parts.length > 0 ? `: ${parts.join(", ")}` : ""}]`;
};

const formatMessageContent = (content: LlmMaterializedPromptMessageV1["content"]): string => {
  if (typeof content === "string") {
    return content;
  }

  return content
    .map((part) => {
      if (part.type === "text") {
        return part.text;
      }
      if (part.type === "image") {
        return formatImageMetadata(part.image);
      }
      return `[${part.summary}]`;
    })
    .join("\n");
};

export const materializedPromptToCopyText = (prompt: LlmMaterializedPromptV1): string => {
  const sections: string[] = [];

  if (prompt.system) {
    sections.push(`SYSTEM\n${prompt.system}`);
  }

  if (prompt.prompt) {
    sections.push(`PROMPT\n${prompt.prompt}`);
  }

  for (const message of prompt.messages ?? []) {
    sections.push(`${message.role.toUpperCase()}\n${formatMessageContent(message.content)}`);
  }

  if (prompt.images?.length) {
    sections.push(`IMAGES\n${prompt.images.map(formatImageMetadata).join("\n")}`);
  }

  if (prompt.tools?.length) {
    sections.push(
      `TOOLS\n${prompt.tools
        .map((tool) => `- ${tool.name}${tool.description ? `: ${tool.description}` : ""}`)
        .join("\n")}`,
    );
  }

  if (prompt.options && Object.keys(prompt.options).length > 0) {
    sections.push(`OPTIONS\n${formatJson(prompt.options)}`);
  }

  return sections.join("\n\n");
};

export const createLlmMaterializedPrompt = (
  input: Omit<LlmMaterializedPromptV1, "type" | "version">,
): LlmMaterializedPromptV1 =>
  llmMaterializedPromptV1Schema.parse({
    type: "llm.materialized_prompt",
    version: 1,
    ...input,
  });
